import Typography from '@alltrails/core/components/Typography';
import ShowMoreLinks from '@alltrails/layout/components/ShowMoreLinks/ShowMoreLinks';
import LinksColumn from '@alltrails/seo/components/LinksColumn';
import type { SeoLinkItem } from '@alltrails/types/SeoLinkItem';
import { DEFAULT_MAX_LINKS_PER_SECTION, SEO_TEST_MINIMIZE_COLLAPSED_COUNT } from '../seoLinksHelpers';
import styles from './DesktopLinkSection.module.scss';

type DesktopLinkSectionProps = {
  title: string;
  links: SeoLinkItem[];
  isBot?: boolean;
  seoTestAddTrailsToLocationLinks?: boolean;
  seoTestAddHikingTrailsToLocationLinks?: boolean;
  seoTestMinimizeSeoLinks?: boolean;
};

const DesktopLinkSection = ({
  title,
  links,
  isBot = false,
  seoTestAddTrailsToLocationLinks,
  seoTestAddHikingTrailsToLocationLinks,
  seoTestMinimizeSeoLinks
}: DesktopLinkSectionProps) => {
  const firstBatch = links.slice(0, 8);
  const secondBatch = links.slice(8, DEFAULT_MAX_LINKS_PER_SECTION);

  return (
    <div>
      <Typography variant="heading200" component="h3" mt="0" className={styles.title}>
        {title}
      </Typography>
      <div className={styles.linkBlockColumns}>
        {seoTestMinimizeSeoLinks ? (
          <ShowMoreLinks linkItems={links} collapsedLimit={SEO_TEST_MINIMIZE_COLLAPSED_COUNT} isBot={isBot}>
            {(visibleLinkItems, showMoreSlot, isExpanded) => {
              let visibleFirstBatch = visibleLinkItems.slice(0, 8);
              let visibleSecondBatch = visibleLinkItems.slice(8, DEFAULT_MAX_LINKS_PER_SECTION);

              // two links in first column; third link + show more in second column (collapsed minimized state)
              const splitCollapsedWithToggle = showMoreSlot && !isExpanded;
              const showMoreToggleSlot = showMoreSlot ? <div className={styles.showMoreToggle}>{showMoreSlot}</div> : null;

              if (splitCollapsedWithToggle) {
                const collapsedRow = visibleFirstBatch;
                visibleSecondBatch = collapsedRow.slice(2);
                visibleFirstBatch = collapsedRow.slice(0, 2);
              }

              return (
                <>
                  <div className={styles.linksColumn}>
                    <LinksColumn
                      links={visibleFirstBatch}
                      seoTestAddTrailsToLocationLinks={seoTestAddTrailsToLocationLinks}
                      seoTestAddHikingTrailsToLocationLinks={seoTestAddHikingTrailsToLocationLinks}
                    />
                    {visibleSecondBatch.length === 0 && visibleFirstBatch.length > SEO_TEST_MINIMIZE_COLLAPSED_COUNT && showMoreToggleSlot}
                  </div>
                  {!!visibleSecondBatch.length && (
                    <div className={styles.linksColumn}>
                      <LinksColumn
                        links={visibleSecondBatch}
                        seoTestAddTrailsToLocationLinks={seoTestAddTrailsToLocationLinks}
                        seoTestAddHikingTrailsToLocationLinks={seoTestAddHikingTrailsToLocationLinks}
                      />
                      {showMoreToggleSlot}
                    </div>
                  )}
                </>
              );
            }}
          </ShowMoreLinks>
        ) : (
          <>
            <div className={styles.linksColumn}>
              <LinksColumn
                links={firstBatch}
                seoTestAddTrailsToLocationLinks={seoTestAddTrailsToLocationLinks}
                seoTestAddHikingTrailsToLocationLinks={seoTestAddHikingTrailsToLocationLinks}
              />
            </div>
            {!!secondBatch.length && (
              <div className={styles.linksColumn}>
                <LinksColumn
                  links={secondBatch}
                  seoTestAddTrailsToLocationLinks={seoTestAddTrailsToLocationLinks}
                  seoTestAddHikingTrailsToLocationLinks={seoTestAddHikingTrailsToLocationLinks}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DesktopLinkSection;
